import {
    Box, IconButton, InputAdornment, Table, TableBody,
    TableCell, TableContainer, TableHead, TablePagination, TableRow,
    TextField, Tooltip, Typography, Button, CircularProgress,
    Autocomplete, Dialog, DialogTitle, DialogContent
} from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import SearchIcon from '@mui/icons-material/Search'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined'
import SyncAltIcon from '@mui/icons-material/SyncAlt'
import VisibilityIcon from '@mui/icons-material/Visibility'
import CloseIcon from '@mui/icons-material/Close'
import { useNavigate } from 'react-router-dom'
import {
    ApiGetWithdrawalRequests,
    ApiApproveReferralDeposit,
    ApiRejectReferralDeposit
} from '../../apiService/Core/Apicall'
import PopupDialog from '../../Components/Popups/ConfirmDialog'
import { useDateRange } from '../../hooks/useDateRange'
import DateRangeFilter from '../../Components/Common/DateRangeFilter'
import { usePersistedState } from '../../hooks/userPersistedState'
import CasinoIcon from '@mui/icons-material/Casino';

interface Fees {
    platform: number
    provider: number
    total: number
}

interface Transaction {
    _id: string
    transactionId: string
    amount: number
    netAmount: number
    status: string
    paymentProvider: string
    description: string
    direction: 'INCOMING' | 'OUTGOING'
    type: string
    currency: string
    fees: Fees
    createdAt: string
    userId: string | {
        _id: string,
        firstName: string,
        lastName: string,
        email: string
    }
}

interface Pagination {
    total: number
    page: number
    limit: number
    totalPages: number
    hasNextPage: boolean
    hasPrevPage: boolean
}

const formatAmount = (amount: number) =>
    amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const formatDate = (date: string) =>
    new Date(date)?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })

const formatTime = (date: string) =>
    new Date(date)?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })

const getUserInfo = (userId: Transaction['userId']) => {
    if (typeof userId === 'object' && userId !== null) {
        return { name: `${userId.firstName} ${userId.lastName}`, email: userId.email }
    }
    return { name: '—', email: '—' }
}

const statusMap: Record<string, { color: string; bg: string; border: string }> = {
    COMPLETED: { color: '#10b981', bg: '#10b98118', border: '#10b98150' },
    PENDING: { color: '#3b82f6', bg: '#3b82f618', border: '#3b82f650' },
    PROCESSING: { color: '#f59e0b', bg: '#f59e0b18', border: '#f59e0b50' },
    FAILED: { color: '#ef4444', bg: '#ef444418', border: '#ef444450' },
    APPROVED: { color: '#10b981', bg: '#10b98118', border: '#10b98150' },
    REJECTED: { color: '#ef4444', bg: '#ef444418', border: '#ef444450' },
    CANCELLED: { color: '#9ca3af', bg: '#9ca3af18', border: '#9ca3af50' },
}

const StatusPill: React.FC<{ status: string; tooltip?: string }> = ({ status, tooltip }) => {
    const s = statusMap[status] || { color: '#9a9a9a', bg: '#9a9a9a18', border: '#9a9a9a50' }

    const pill = (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.6,
            px: 1.5, py: 0.4, borderRadius: '6px', fontSize: '11px', fontWeight: 700,
            color: s.color, backgroundColor: s.bg, border: `1px solid ${s.border}`,
            letterSpacing: '0.03em', whiteSpace: 'nowrap',
            cursor: tooltip ? 'help' : 'default',
            borderBottom: tooltip ? `1px dashed ${s.color}60` : `1px solid ${s.border}`,
        }}>
            <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
            {status}
            {tooltip && (
                <Box sx={{ fontSize: 9, opacity: 0.7, ml: 0.3 }}>ⓘ</Box>
            )}
        </Box>
    )

    if (tooltip) {
        return (
            <Tooltip
                title={
                    <Box>
                        <Typography fontSize={10} color="#aaa" mb={0.3} letterSpacing="0.06em" textTransform="uppercase">
                            Reason
                        </Typography>
                        <Typography fontSize={12}>{tooltip}</Typography>
                    </Box>
                }
                arrow
                placement="top"
            >
                {pill}
            </Tooltip>
        )
    }

    return pill
}

const StatCard: React.FC<{ label: string; value: string; sub?: string; color: string; icon: React.ReactNode }> = ({ label, value, sub, color, icon }) => (
    <Box sx={{
        background: 'linear-gradient(135deg, #2a2a2a 0%, #222 100%)',
        border: '1px solid #333',
        borderRadius: '14px',
        p: 2.5,
        position: 'relative',
        overflow: 'hidden',
        transition: 'border-color 0.25s',
        '&:hover': { borderColor: color + '60' },
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0, left: 0, right: 0,
            height: '2px',
            background: `linear-gradient(90deg, ${color}00, ${color}, ${color}00)`,
        },
    }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography fontSize={10} color="#6f6f6f" textTransform="uppercase" letterSpacing="0.1em">{label}</Typography>
            <Box sx={{ color, opacity: 0.7 }}>{icon}</Box>
        </Box>
        <Typography fontSize={20} fontWeight={800} color={color} sx={{ letterSpacing: '-0.02em' }}>{value}</Typography>
        {sub && <Typography fontSize={10} color="#5a5a5a" mt={0.5}>{sub}</Typography>}
    </Box>
)

const Referrals: React.FC = () => {
    const {
        range, customStart, setCustomStart, customEnd, setCustomEnd, handleRangeChange, buildQuery,
    } = useDateRange('referrals', 'all')

    const [transactions, setTransactions] = useState<Transaction[]>([])
    const [pagination, setPagination] = useState<Pagination>({ total: 0, page: 1, limit: 10, totalPages: 1, hasNextPage: false, hasPrevPage: false })
    const [search, setSearch] = usePersistedState('referrals:search', '')
    const [page, setPage] = usePersistedState('referrals:page', 0)
    const [rowsPerPage, setRowsPerPage] = usePersistedState('referrals:rowsPerPage', 10)
    const [approvingId, setApprovingId] = useState<string | null>(null)
    const [rejectingId, setRejectingId] = useState<string | null>(null)

    const [stats, setStats] = useState({ total: 0, count: 0, pendingCount: 0, pendingTotal: 0 })

    const STATUS_OPTIONS = ['PENDING', 'COMPLETED', 'REJECTED', 'CANCELLED']
    const [status, setStatus] = usePersistedState('referrals:status', '')
    const [debouncedSearch, setDebouncedSearch] = useState(search)

    const [errorPopup, setErrorPopup] = useState<{ open: boolean; message: string }>({ open: false, message: '' })
    const [successPopup, setSuccessPopup] = useState<{ open: boolean; message: string }>({ open: false, message: '' })

    const [rejectReasonOpen, setRejectReasonOpen] = useState(false)
    const [rejectReason, setRejectReason] = useState('')
    const [rejectReasonError, setRejectReasonError] = useState('')
    const [pendingRejectId, setPendingRejectId] = useState<string | null>(null)
    const [confirmRejectOpen, setConfirmRejectOpen] = useState(false)
    const [confirmApproveOpen, setConfirmApproveOpen] = useState(false)
    const [pendingApproveTx, setPendingApproveTx] = useState<Transaction | null>(null)

    const navigate = useNavigate()

    const fetchTransactions = useCallback(async () => {
        try {
            const dateQuery = buildQuery()
            if (dateQuery === null) return

            const params = new URLSearchParams()
            params.append('direction', 'INCOMING')
            params.append('type', 'deposit')
            params.append('paymentProvider', 'REFERRAL')
            params.append('page', String(page + 1))
            params.append('limit', String(rowsPerPage))
            if (debouncedSearch) params.append('search', debouncedSearch)
            if (status) params.append('status', status)
            if (dateQuery) {
                new URLSearchParams(dateQuery).forEach((value, key) => params.append(key, value))
            }
            const res = await ApiGetWithdrawalRequests(`?${params.toString()}`)
            if (res?.status) {
                const txns: Transaction[] = res.data?.transactions || []
                setTransactions(txns)
                setPagination(res.data?.pagination || {})
                setStats({
                    total: res.data?.stats?.totalAmount ?? 0,
                    count: res.data?.pagination?.total ?? txns.length,
                    pendingCount: res.data?.stats?.pendingCount ?? 0,
                    pendingTotal: res.data?.stats?.pendingTotal ?? 0,
                })
            }
        } catch (error: any) {
            setErrorPopup({
                open: true,
                message: error?.message || error?.response?.data?.message || error || 'Failed to fetch referral deposits',
            })
        }
    }, [page, rowsPerPage, debouncedSearch, status, buildQuery])

    useEffect(() => {
        fetchTransactions()
    }, [fetchTransactions])

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search)
        }, 500)
        return () => clearTimeout(timer)
    }, [search])

    const prevFiltersRef = React.useRef({ debouncedSearch, status, range, customStart, customEnd })
    useEffect(() => {
        const prev = prevFiltersRef.current
        const changed =
            prev.debouncedSearch !== debouncedSearch ||
            prev.status !== status ||
            prev.range !== range ||
            (prev.customStart?.valueOf() !== customStart?.valueOf()) ||
            (prev.customEnd?.valueOf() !== customEnd?.valueOf())
        if (changed) setPage(0)
        prevFiltersRef.current = { debouncedSearch, status, range, customStart, customEnd }
    }, [debouncedSearch, status, range, customStart, customEnd, setPage])

    const handleApprove = async (tx: Transaction) => {
        const id = tx.transactionId
        setApprovingId(id)
        try {
            const data = { transactionId: id }
            const res = await ApiApproveReferralDeposit(data)
            if (res?.status) {
                setSuccessPopup({
                    open: true,
                    message: res?.message || 'Referral deposit approved successfully',
                })
                fetchTransactions()
                await new Promise(r => setTimeout(r, 800))
                setTransactions(prev => prev.map(t => t.transactionId === id ? { ...t, status: 'COMPLETED' } : t))
            }
        } catch (error: any) {
            setErrorPopup({
                open: true,
                message: error?.message || error?.response?.data?.message || error || 'Failed to approve',
            })
        } finally {
            setApprovingId(null)
        }
    }

    const handleReject = async (id: string, reason: string) => {
        setRejectingId(id)
        try {
            const data = { transactionId: id, rejectReason: reason }
            const res = await ApiRejectReferralDeposit(data)
            if (res?.status) {
                setSuccessPopup({
                    open: true,
                    message: res?.message || 'Referral deposit rejected successfully',
                })
                fetchTransactions()
                await new Promise(r => setTimeout(r, 800))
                setTransactions(prev => prev.map(t => t.transactionId === id ? { ...t, status: 'REJECTED' } : t))
            }
        } catch (error: any) {
            setErrorPopup({
                open: true,
                message: error?.message || error?.response?.data?.message || error || 'Failed to reject',
            })
        } finally {
            setRejectingId(null)
        }
    }

    const pendingCount = stats.pendingCount

    return (
        <div className='common-layout flex-column'>
            <Box className="border-10 p-2 px-4 mixed-orange mb-4 both-circle d-flex align-items-center justify-content-between">
                <Box className="d-flex gap-3 align-items-center">
                    <Box>
                        <Typography className='fs-24 text-white fw-medium' sx={{ lineHeight: 1.1 }}>Referrals</Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                        variant="contained"
                        startIcon={<CasinoIcon sx={{ fontSize: 14 }} />}
                        onClick={() => navigate('/admin/spin-logs')}
                        sx={{
                            borderRadius: '10px', fontWeight: 700, px: 1.5, py: 0.5,
                            background: 'linear-gradient(135deg, #a78bfa, #7c3aed)',
                            '&:hover': { background: 'linear-gradient(135deg, #9061f9, #6d28d9)' },
                        }}
                    >
                        Spin Logs
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<CasinoIcon sx={{ fontSize: 14 }} />}
                        onClick={() => navigate('/admin/spin-sequence-monitor')}
                        sx={{
                            borderRadius: '10px', fontWeight: 700, px: 1.5, py: 0.5,
                            background: 'linear-gradient(135deg, #a78bfa, #7c3aed)',
                            '&:hover': { background: 'linear-gradient(135deg, #9061f9, #6d28d9)' },
                        }}
                    >
                        Spin Sequence Monitor
                    </Button>
                    {pendingCount > 0 && (
                        <Box sx={{
                            background: '#ffffff60', border: '1px solid red',
                            borderRadius: '8px', px: 2, py: 0.8,
                            display: 'flex', alignItems: 'center', gap: 1
                        }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: 'red', animation: 'pulse 1.5s infinite' }} />
                            <Typography sx={{ color: 'red', fontSize: 12, fontWeight: 700 }}>
                                {pendingCount} Pending Referral{pendingCount > 1 ? 's' : ''}
                            </Typography>
                        </Box>
                    )}

                </Box>
            </Box>

            <DateRangeFilter
                range={range}
                onRangeChange={handleRangeChange}
                customStart={customStart}
                onCustomStartChange={setCustomStart}
                customEnd={customEnd}
                onCustomEndChange={setCustomEnd}
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2, mb: 2, mt: 2 }}>
                <StatCard label="Total Referral Deposits" value={`$${formatAmount(stats.total)}`}
                    sub={`${stats.count} total transactions`} color="#10b981"
                    icon={<ArrowDownwardIcon fontSize="small" />} />
                <StatCard label="Pending Approval" value={`$${formatAmount(stats.pendingTotal)}`}
                    sub={`${stats.pendingCount} awaiting review`} color="#f59e0b"
                    icon={<SyncAltIcon fontSize="small" />} />
                <StatCard label="Transactions (Page)" value={String(transactions.length)}
                    sub={`of ${pagination.total} total`} color="#a78bfa"
                    icon={<AccountBalanceWalletOutlinedIcon fontSize="small" />} />
            </Box>

            <Box className="bg-dark-color border-10 p-0 mt-2" sx={{ overflow: 'hidden' }}>
                <Box sx={{ p: '16px 20px', borderBottom: '1px solid #1e1e1e', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Typography fontSize={13} fontWeight={700} color="#e0e0e0">
                        Referral Deposits
                        {pagination.total > 0 && (
                            <Box component="span" sx={{ ml: 1, background: 'rgba(255,255,255,0.08)', borderRadius: '10px', px: 1, py: 0.2, fontSize: 11 }}>
                                {pagination.total}
                            </Box>
                        )}
                    </Typography>

                    <Box className="d-flex align-items-center gap-2" sx={{ flexWrap: 'wrap' }}>
                        <Autocomplete
                            size="small"
                            value={status}
                            disableClearable={!status}
                            onChange={(_, newValue) => { setStatus(newValue || ''); }}
                            options={STATUS_OPTIONS}
                            renderInput={(params) => (
                                <TextField {...params} placeholder="Status" sx={searchFieldStyle}
                                    InputProps={{ ...params.InputProps, sx: { color: '#fff', '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': { color: '#fff' } } }}
                                />
                            )}
                            PaperComponent={({ children }) => (
                                <Box className="bg-dark-color" sx={{ color: '#fff', '& .MuiAutocomplete-option': { '&:hover': { bgcolor: '#241815' }, "&[aria-selected='true']": { bgcolor: '#241815' } } }}>
                                    {children}
                                </Box>
                            )}
                        />

                        <TextField
                            placeholder="Search by User, Email"
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); }}
                            size="small"
                            sx={searchFieldStyle}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon className='color-primary' />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                </Box>

                <TableContainer sx={{ maxHeight: '60vh' }}>
                    <Table stickyHeader sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid #1e1e1e' } }}>
                        <TableHead>
                            <TableRow>
                                {['#', 'User', 'Transaction ID', 'Date & Time', 'Amount', 'Net Amount', 'Fees', 'Status', 'Referrals', 'Action'].map(h => (
                                    <TableCell key={h} sx={thStyle}>{h}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {transactions.length > 0 ? transactions.map((tx, i) => (
                                <TableRow key={tx._id} sx={trHover}>
                                    <TableCell sx={tdStyle}>{page * rowsPerPage + i + 1}</TableCell>
                                    <TableCell
                                        sx={{ ...tdStyle, cursor: 'pointer' }}
                                        onClick={() => {
                                            const u = tx.userId as { _id: string; firstName: string; lastName: string; email: string }
                                            navigate('/admin/user-activity', {
                                                state: { userId: u._id, userName: `${u.firstName} ${u.lastName}` }
                                            })
                                        }}
                                    >
                                        <Typography fontSize={13} fontWeight={600} color="#e0e0e0">
                                            {getUserInfo(tx.userId).name}
                                        </Typography>
                                        <Typography fontSize={10} color="#5a5a5a">{getUserInfo(tx.userId).email}</Typography>
                                    </TableCell>
                                    <TableCell sx={tdStyle}>
                                        <Tooltip title={tx.transactionId} arrow>
                                            <Typography fontSize={11} color="#7a7a7a" sx={{ fontFamily: 'monospace', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {tx.transactionId}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell sx={tdStyle}>
                                        <Typography fontSize={13} color="#e0e0e0">{formatDate(tx.createdAt)}</Typography>
                                        <Typography fontSize={10} color="#5a5a5a">{formatTime(tx.createdAt)}</Typography>
                                    </TableCell>
                                    <TableCell sx={tdStyle}>
                                        <Typography fontSize={14} fontWeight={700} color="#10b981">
                                            +${formatAmount(tx.amount)}
                                        </Typography>
                                        <Box sx={{ display: 'inline-block', px: 1, py: 0.2, borderRadius: '4px', fontSize: 9, fontWeight: 700, color: '#f2674a', background: '#f2674a12', border: '1px solid #f2674a25', mt: 0.3 }}>
                                            {tx.currency}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={tdStyle}>
                                        <Typography fontSize={13} color="#9a9a9a">${formatAmount(tx.netAmount)}</Typography>
                                    </TableCell>
                                    <TableCell sx={tdStyle}>
                                        {tx.fees?.total > 0 ? (
                                            <Tooltip title={`Platform: $${formatAmount(tx.fees.platform)} | Provider: $${formatAmount(tx.fees.provider)}`} arrow>
                                                <Typography fontSize={12} color="#9a9a9a">${formatAmount(tx.fees.total)}</Typography>
                                            </Tooltip>
                                        ) : (
                                            <Typography fontSize={11} color="#3a3a3a">-</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell sx={tdStyle}><StatusPill status={tx.status} /></TableCell>
                                    <TableCell sx={tdStyle}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Tooltip title="View User's Referrals" arrow>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                                                    onClick={() => {
                                                        const u = tx.userId as { _id: string; firstName: string; lastName: string; email: string }
                                                        navigate('/admin/referral-summary', { state: { userId: u._id, userName: `${u.firstName} ${u.lastName}` } })
                                                    }}
                                                    sx={{
                                                        fontSize: 11, fontWeight: 700, borderRadius: '7px',
                                                        color: '#ff6a3d', border: '1px solid #ff6a3d40',
                                                        background: '#ff6a3d10', px: 1.5, py: 0.5, minWidth: 80,
                                                        textTransform: 'none',
                                                        '&:hover': { background: '#ff6a3d20', borderColor: '#ff6a3d' },
                                                    }}
                                                >
                                                    View
                                                </Button>
                                            </Tooltip>
                                            <Tooltip title="View User's Spin History" arrow>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<CasinoIcon sx={{ fontSize: 14 }} />}
                                                    onClick={() => {
                                                        const u = tx.userId as { _id: string; firstName: string; lastName: string; email: string }
                                                        navigate('/admin/user-spin-history', { state: { userId: u._id, userName: `${u.firstName} ${u.lastName}` } })
                                                    }}
                                                    sx={{
                                                        fontSize: 11, fontWeight: 700, borderRadius: '7px',
                                                        color: '#a78bfa', border: '1px solid #a78bfa40',
                                                        background: '#a78bfa10', px: 1.5, py: 0.5, minWidth: 80,
                                                        textTransform: 'none',
                                                        '&:hover': { background: '#a78bfa20', borderColor: '#a78bfa' },
                                                    }}
                                                >
                                                    Spins
                                                </Button>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={tdStyle}>
                                        {tx.status === 'PENDING' ? (
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    disabled={approvingId === tx.transactionId}
                                                    onClick={() => { setPendingApproveTx(tx); setConfirmApproveOpen(true) }}
                                                    sx={{
                                                        background: 'linear-gradient(135deg, #10b981, #059669)',
                                                        color: '#fff', fontWeight: 700, fontSize: 11,
                                                        borderRadius: '7px', px: 2, py: 0.6,
                                                        '&:hover': { background: 'linear-gradient(135deg, #059669, #047857)' },
                                                        '&:disabled': { opacity: 0.5 }, minWidth: 90,
                                                    }}
                                                >
                                                    {approvingId === tx.transactionId ? <CircularProgress size={12} sx={{ color: '#fff' }} /> : '✓ Approve'}
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    disabled={approvingId === tx.transactionId || rejectingId === tx.transactionId}
                                                    onClick={() => {
                                                        setPendingRejectId(tx.transactionId)
                                                        setRejectReason('')
                                                        setRejectReasonError('')
                                                        setRejectReasonOpen(true)
                                                    }}
                                                    sx={{
                                                        background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                                                        color: '#fff', fontWeight: 700, fontSize: 11,
                                                        borderRadius: '7px', px: 2, py: 0.6,
                                                        '&:hover': { background: 'linear-gradient(135deg, #dc2626, #b91c1c)' },
                                                        '&:disabled': { opacity: 0.5 }, minWidth: 90,
                                                    }}
                                                >
                                                    ✕ Reject
                                                </Button>
                                            </Box>
                                        ) : (
                                            <Typography fontSize={11} color="#3a3a3a" fontStyle="italic">—</Typography>
                                        )}
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={10} align="center" sx={{ py: 8, background: '#1a1a1a', border: 'none' }}>
                                        <ArrowDownwardIcon sx={{ color: '#2f2f2f', fontSize: 40, mb: 1 }} />
                                        <Typography color="#4a4a4a" fontSize={13}>No referral deposits found</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={pagination.total}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    showFirstButton showLastButton
                    sx={paginationStyle}
                />
            </Box>

            <Dialog
                open={rejectReasonOpen}
                onClose={() => { setRejectReasonOpen(false); setRejectReason(''); setRejectReasonError('') }}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: '#242424',
                        borderRadius: '16px',
                        boxShadow: '0 0 60px rgba(239,68,68,.25)',
                        border: '1px solid #333',
                        overflow: 'hidden',
                    }
                }}
            >
                <DialogTitle sx={{
                    background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                    color: '#fff', display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between', fontWeight: 700, mb: 0, fontSize: 15,
                    boxShadow: '0 4px 20px rgba(239,68,68,.4)',
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 32, height: 32, borderRadius: '8px',
                            background: 'rgba(255,255,255,0.2)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                            <CloseIcon fontSize="small" />
                        </Box>
                        Reject Referral Deposit
                    </Box>
                    <IconButton onClick={() => setRejectReasonOpen(false)} size="small">
                        <CloseIcon sx={{ color: '#fff', fontSize: 18 }} />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ backgroundColor: '#242424', pt: '20px !important' }}>
                    <Typography fontSize={11} color="#5a5a5a" mb={1.5} letterSpacing="0.08em" textTransform="uppercase">
                        Reason for rejection
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        maxRows={6}
                        placeholder="Enter the reason for rejecting this referral deposit…"
                        value={rejectReason}
                        onChange={(e) => { setRejectReason(e.target.value); setRejectReasonError('') }}
                        error={!!rejectReasonError}
                        helperText={rejectReasonError}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '10px', color: '#fff',
                                '& fieldset': { borderColor: '#333' },
                                '&:hover fieldset': { borderColor: '#ef444450' },
                                '&.Mui-focused fieldset': { borderColor: '#ef4444' },
                            },
                            '& textarea': { color: '#fff', fontSize: 13 },
                            '& textarea::placeholder': { color: '#555', opacity: 1 },
                            '& .MuiFormHelperText-root': { color: '#ef4444', fontSize: 11 },
                        }}
                    />

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
                        {[
                            'Suspicious referral activity',
                            'Self-referral detected',
                            'Referral criteria not met',
                            'Duplicate request',
                            'Account under review',
                        ].map((reason) => (
                            <Box
                                key={reason}
                                onClick={() => { setRejectReason(reason); setRejectReasonError('') }}
                                sx={{
                                    px: 1.5, py: 0.6, borderRadius: '7px', cursor: 'pointer',
                                    fontSize: 11, fontWeight: 600, border: '1px solid #333',
                                    color: rejectReason === reason ? '#ef4444' : '#6f6f6f',
                                    background: rejectReason === reason ? '#ef444415' : '#1e1e1e',
                                    borderColor: rejectReason === reason ? '#ef444450' : '#333',
                                    transition: 'all 0.15s',
                                    '&:hover': { borderColor: '#ef444450', color: '#ef4444' }
                                }}
                            >
                                {reason}
                            </Box>
                        ))}
                    </Box>
                </DialogContent>

                <Box sx={{ px: 3, pb: 3, pt: 1, display: 'flex', gap: 1.5 }}>
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => { setRejectReasonOpen(false); setRejectReason(''); setRejectReasonError('') }}
                        sx={{ borderColor: '#333', color: '#7a7a7a', borderRadius: '10px', '&:hover': { borderColor: '#555', color: '#fff' } }}
                    >
                        Cancel
                    </Button>
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={() => {
                            if (!rejectReason.trim()) {
                                setRejectReasonError('Please provide a reason for rejection')
                                return
                            }
                            setRejectReasonOpen(false)
                            setConfirmRejectOpen(true)
                        }}
                        sx={{
                            background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                            borderRadius: '10px', fontWeight: 700,
                            boxShadow: '0 4px 16px rgba(239,68,68,.4)',
                            '&:hover': { background: 'linear-gradient(135deg, #dc2626, #b91c1c)' },
                        }}
                    >
                        Continue
                    </Button>
                </Box>
            </Dialog>

            <style>{`
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.3; }
                }
            `}</style>

            <PopupDialog
                open={errorPopup.open}
                onClose={() => setErrorPopup({ open: false, message: '' })}
                onConfirm={() => setErrorPopup({ open: false, message: '' })}
                variant="error"
                title="Something Went Wrong"
                message={errorPopup.message}
                confirmText="Try Again"
                cancelText="Dismiss"
            />
            <PopupDialog
                open={successPopup.open}
                onClose={() => setSuccessPopup({ open: false, message: '' })}
                onConfirm={() => setSuccessPopup({ open: false, message: '' })}
                variant="success"
                title="Success"
                message={successPopup.message}
                confirmText="Done"
                cancelText="Close"
            />
            <PopupDialog
                open={confirmApproveOpen}
                onClose={() => setConfirmApproveOpen(false)}
                onConfirm={() => {
                    setConfirmApproveOpen(false)
                    if (pendingApproveTx) handleApprove(pendingApproveTx)
                }}
                loading={!!approvingId}
                variant="deposit"
                title="Confirm Approval"
                message="You are about to approve this referral deposit."
                confirmText="Confirm Approve"
                details={[
                    { label: 'Transaction ID', value: pendingApproveTx?.transactionId ?? '—', mono: true },
                    { label: 'Amount', value: pendingApproveTx ? `$${formatAmount(pendingApproveTx.amount)}` : '—' },
                    {
                        label: 'Timestamp',
                        value: new Date().toLocaleString('en-US', {
                            month: 'short', day: 'numeric', year: 'numeric',
                            hour: '2-digit', minute: '2-digit'
                        }),
                        mono: true,
                    },
                ]}
                notice="Approving will credit this referral reward to the user's account balance."
            />
            <PopupDialog
                open={confirmRejectOpen}
                onClose={() => setConfirmRejectOpen(false)}
                onConfirm={() => {
                    setConfirmRejectOpen(false)
                    if (pendingRejectId) handleReject(pendingRejectId, rejectReason)
                }}
                loading={!!rejectingId}
                variant="error"
                title="Confirm Rejection"
                message="You are about to reject this referral deposit. This action cannot be undone."
                confirmText="Confirm Reject"
                details={[
                    { label: 'Transaction ID', value: pendingRejectId ?? '—', mono: true },
                    { label: 'Reason', value: rejectReason },
                    {
                        label: 'Timestamp',
                        value: new Date().toLocaleString('en-US', {
                            month: 'short', day: 'numeric', year: 'numeric',
                            hour: '2-digit', minute: '2-digit'
                        }),
                        mono: true,
                    },
                ]}
                notice="The user will be notified of this rejection along with the reason provided."
            />
        </div>
    )
}

export default Referrals

const thStyle = {
    backgroundColor: '#241815',
    color: '#ff6a3d',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    whiteSpace: 'nowrap' as const,
    py: 1.5,
    borderBottom: '1px solid #2a2a2a !important',
    position: 'sticky' as const,
    top: 0,
    zIndex: 2,
}

const tdStyle = {
    color: '#c0c0c0',
    py: 1.4,
    fontSize: 13,
    whiteSpace: 'nowrap' as const,
}

const trHover = {
    transition: 'background 0.15s',
    '&:hover': { background: '#ff6a3d08' },
}

const searchFieldStyle = {
    width: 280,
    background: 'transparent',
    borderRadius: '7px',
    border: '1px solid #898989',
    transition: 'border-color 0.3s',
    '&:hover': { border: '1px solid #ff6a3d' },
    '&:focus-within': { border: '1px solid #ff6a3d' },
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '& input': { color: '#fff', fontSize: 13 },
    '& input::placeholder': { color: '#555', opacity: 1 },
}

const paginationStyle = {
    color: '#6a6a6a', borderTop: '1px solid #252525',
    '& .MuiTablePagination-toolbar': { justifyContent: 'center' },
    '& .MuiTablePagination-selectLabel': { color: '#6a6a6a', margin: 0 },
    '& .MuiTablePagination-displayedRows': { color: '#6a6a6a', margin: 0 },
    '& .MuiSelect-select': { color: '#6a6a6a' },
    '& .MuiSelect-icon': { color: '#6a6a6a' },
    '& .MuiIconButton-root': { color: '#6a6a6a' },
    '& .MuiIconButton-root.Mui-disabled': { color: '#333' },
}